export const AZIENDE_PAGES = [
  {
    path: '/soluzioni/aziende',
    kind: 'area',
    area: 'Aziende',
    areaPath: '/soluzioni/aziende',
    eyebrow: 'Area Aziende',
    title: 'Assicurazioni aziendali in Svizzera: proteggere persone, responsabilità e continuità.',
    summary: 'Analisi dei rischi e programma assicurativo per aziende e PMI: collaboratori, RC, beni, cyber, interruzione d’esercizio e previdenza coordinati.',
    seoTitle: 'Assicurazioni aziendali e PMI in Svizzera',
    seoDescription: 'Broker assicurativo per aziende e PMI in Svizzera: analisi rischi, LAINF, LPP, RC, cyber, beni e interruzione d’esercizio. Richiedi una revisione.',
    keywords: ['assicurazioni aziendali svizzera', 'assicurazioni PMI', 'broker assicurativo aziende', 'RC aziendale', 'LAINF LPP azienda'],
    image: '/assets/soluzione-aziende.png',
    updated: 'Giugno 2026',
    readingTime: '5 min',
    intro: [
      'Un’impresa non ha bisogno di un elenco di polizze: ha bisogno di sapere quali eventi possono compromettere la sua continuità e come proteggersi. Il nostro lavoro parte da un’analisi dei rischi, non da un catalogo.',
      'La revisione considera obblighi verso i collaboratori, responsabilità verso terzi, beni e infrastrutture, dati, dipendenza da persone chiave e capacità dell’azienda di sostenere un fermo o una perdita importante.',
    ],
    sections: [
      {
        id: 'aree-rischio', type: 'cards', title: 'L\u2019analisi per aree di rischio',
        cards: [
          { title: 'Persone', text: 'Infortuni, malattia, previdenza dei collaboratori e protezione del titolare.' },
          { title: 'Responsabilità', text: 'Danni a terzi, errori professionali, responsabilità degli organi societari.' },
          { title: 'Beni', text: 'Locali, macchinari, inventario, elettronica, merci in trasporto.' },
          { title: 'Continuità', text: 'Interruzione d\u2019esercizio, perdita di reddito, dipendenza da fornitori chiave.' },
          { title: 'Dati e cyber', text: 'Attacchi informatici, perdita dati, responsabilità privacy, frode.' },
          { title: 'Mobilità', text: 'Veicoli aziendali, flotte, trasferte e viaggi d\u2019affari.' },
        ],
      },
      {
        id: 'metodo', type: 'timeline', title: 'Il metodo di consulenza aziendale',
        steps: [
          { label: '01', title: 'Intervista', text: 'Ascoltiamo il titolare: attività, clienti, processi, preoccupazioni.' },
          { label: '02', title: 'Raccolta', text: 'Analizziamo i contratti esistenti e la documentazione disponibile.' },
          { label: '03', title: 'Mappatura', text: 'Individuiamo rischi, lacune e doppioni nelle coperture attuali.' },
          { label: '04', title: 'Confronto', text: 'Confrontiamo le soluzioni sul mercato in base alle priorità emerse.' },
          { label: '05', title: 'Implementazione', text: 'Attiviamo il programma e coordiniamo le compagnie.' },
          { label: '06', title: 'Revisione', text: 'Rivediamo periodicamente il programma, a ogni cambiamento aziendale.' },
        ],
      },
      {
        id: 'percorsi', type: 'paths', title: 'Approfondimenti per le aziende',
        links: [
          { href: '/guide/aziende/aprire-un-azienda', label: 'Aprire un\u2019azienda', text: 'La checklist pratica per chi sta avviando un\u2019attività.' },
          { href: '/guide/aziende/forme-giuridiche-svizzera', label: 'Forme giuridiche', text: 'Ditta individuale, Sagl, SA: conseguenze operative e assicurative.' },
          { href: '/soluzioni/aziende/tipologie-assicurazioni', label: 'Tipologie di assicurazione', text: 'Le categorie di copertura spiegate in modo comprensibile.' },
        ],
      },
      {
        id: 'settori', type: 'text', title: 'I settori che seguiamo',
        paragraphs: ['Commercio, ristorazione, edilizia, studi professionali, tecnologia, industria, servizi e immobiliare. Ogni settore ha rischi tipici: la mappatura li rende visibili prima che diventino un problema.'],
      },
    ],
    faq: [
      { q: 'Da quando conviene far analizzare i rischi aziendali?', a: 'Idealmente prima dell\u2019apertura, e poi a ogni cambiamento rilevante: assunzioni, nuova sede, nuovi macchinari, espansione.' },
      { q: 'Lavorate anche con piccole imprese?', a: 'Sì: gran parte delle aziende che seguiamo sono piccole e medie imprese, dove la continuità dipende spesso da poche persone.' },
      { q: 'Potete gestire i contratti esistenti?', a: 'Sì: raccogliamo e analizziamo le polizze attuali, individuiamo doppioni e lacune e coordiniamo la transizione verso un programma coerente.' },
    ],
    cta: { title: 'Richiedi un\u2019analisi dei rischi aziendali', text: 'Un primo colloquio per capire dove la tua impresa è esposta e quali sono le priorità.', label: 'Richiedi l\u2019analisi' },
    related: ['/guide/aziende/aprire-un-azienda', '/soluzioni/aziende/tipologie-assicurazioni'],
  },
  {
    path: '/soluzioni/aziende/tipologie-assicurazioni',
    kind: 'service',
    area: 'Aziende',
    areaPath: '/soluzioni/aziende',
    eyebrow: 'Aziende',
    title: 'Le principali tipologie di assicurazione aziendale.',
    summary: 'Le categorie di copertura spiegate in modo comprensibile: per ogni soluzione, il rischio concreto che affronta.',
    updated: 'Giugno 2026',
    readingTime: '8 min',
    intro: [
      'Il mondo delle assicurazioni aziendali è pieno di nomi tecnici. Qui li traduciamo in quello che contano davvero: quale rischio coprono e a chi servono.',
    ],
    sections: [
      {
        id: 'persone', type: 'cards', title: 'Persone',
        cards: [
          { title: 'Assicurazione infortuni (LAINF)', text: 'Obbligatoria per i dipendenti: copre infortuni professionali e, secondo le ore lavorate, anche non professionali.' },
          { title: 'Indennità giornaliera malattia', text: 'Integra i salari in caso di malattia dei collaboratori oltre il periodo di continuazione del salario.' },
          { title: 'Previdenza professionale (LPP)', text: 'Il secondo pilastro per i dipendenti: obbligatorio oltre determinate soglie salariali.' },
          { title: 'Persona chiave', text: 'Protegge l\u2019azienda se viene a mancare una figura decisiva per fatturato o know-how.' },
        ],
      },
      {
        id: 'responsabilita', type: 'cards', title: 'Responsabilità',
        cards: [
          { title: 'RC aziendale', text: 'Danni a terzi causati dall\u2019attività, dai locali o dai collaboratori durante il lavoro.' },
          { title: 'RC professionale', text: 'Errori di consulenza, progettazione o servizio: essenziale per studi e professionisti.' },
          { title: 'RC prodotti', text: 'Danni causati dai prodotti venduti o fabbricati.' },
          { title: 'RC organi societari', text: 'Protegge amministratori e dirigenti dalle conseguenze patrimoniali delle loro decisioni.' },
        ],
      },
      {
        id: 'beni', type: 'cards', title: 'Beni e continuità',
        cards: [
          { title: 'Inventario e macchinari', text: 'Incendio, acqua, furto, eventi naturali su locali, attrezzature e merce.' },
          { title: 'Elettronica', text: 'Guasti e danni a server, computer e impianti tecnici.' },
          { title: 'Trasporto merci', text: 'La merce durante il trasporto, proprio o affidato a terzi.' },
          { title: 'Interruzione d\u2019esercizio', text: 'Copre perdita di margine e costi fissi se l\u2019attività si ferma dopo un danno assicurato.' },
        ],
      },
      {
        id: 'cyber-giuridica', type: 'cards', title: 'Dati, cyber e protezione giuridica',
        cards: [
          { title: 'Cyber risk', text: 'Attacchi informatici, perdita dati, interruzione sistemi, costi di ripristino e responsabilità privacy.' },
          { title: 'Frode informatica', text: 'Truffe via email, pagamenti manipolati, ingegneria sociale.' },
          { title: 'Protezione giuridica', text: 'Controversie contrattuali, lavoro, locazione, incasso e circolazione.' },
          { title: 'Veicoli e flotte', text: 'RC, casco e gestione coordinata dei veicoli aziendali.' },
        ],
      },
      {
        id: 'priorita', type: 'warning', title: 'Come stabilire le priorità',
        text: 'Non tutto va assicurato subito: prima i rischi che possono compromettere la continuità dell\u2019impresa (responsabilità, reddito del titolare, interruzione d\u2019esercizio), poi i rischi frequenti, infine le estensioni accessorie. L\u2019ordine esatto dipende dalla tua attività: per questo serve una mappatura.',
      },
    ],
    faq: [
      { q: 'Quali assicurazioni aziendali sono obbligatorie?', a: 'Dipende da attività e collaboratori: infortuni e previdenza professionale sono obbligatorie oltre determinate soglie; altre coperture sono facoltative ma spesso decisive. La verifica va fatta sul caso concreto.' },
      { q: 'Una piccola impresa serve davvero il cyber?', a: 'Le PMI sono tra i bersagli più frequenti degli attacchi, proprio perché meno protette. Se tratti dati di clienti o dipendi dai sistemi per fatturare, vale la pena valutarla.' },
      { q: 'Si può assicurare tutto con un\u2019unica compagnia?', a: 'A volte conviene, a volte no: alcune compagnie sono forti sui beni, altre sulle persone. Come broker confrontiamo e combiniamo le soluzioni più adatte.' },
    ],
    cta: { title: 'Costruisci la mappa dei rischi della tua azienda', text: 'Ti aiutiamo a capire quali di queste categorie contano davvero per la tua attività, e in che ordine affrontarle.', label: 'Costruisci la mappa dei rischi' },
    related: ['/guide/aziende/aprire-un-azienda', '/guide/aziende/forme-giuridiche-svizzera'],
  },
  {
    path: '/guide/aziende/aprire-un-azienda',
    kind: 'guide',
    area: 'Aziende',
    areaPath: '/soluzioni/aziende',
    eyebrow: 'Guida pratica · Aziende',
    title: 'Cosa considerare quando si apre un\u2019azienda.',
    summary: 'La checklist pratica per chi sta avviando un\u2019attività: rischi da mappare, coperture obbligatorie e budget assicurativo.',
    updated: 'Giugno 2026',
    readingTime: '8 min',
    intro: [
      'Aprire un\u2019attività significa concentrarsi su clienti e fatturato. Ma basta un sinistro nei primi mesi — un danno a un cliente, un furto, una malattia del titolare — per mettere in discussione tutto il progetto. Ecco come partire protetti senza sprechi.',
    ],
    sections: [
      {
        id: 'prima-apertura', type: 'checklist', title: 'Prima dell\u2019apertura: le domande da porsi',
        items: [
          'Quale attività svolgo concretamente e per quali clienti?',
          'Quale fatturato stimo per il primo anno?',
          'Avrò collaboratori? Da subito o più avanti?',
          'Ho una sede, attrezzature, veicoli?',
          'Tratto dati di clienti o dipendo da sistemi informatici?',
          'L\u2019attività dipende interamente da me?',
        ],
      },
      {
        id: 'rischi', type: 'cards', title: 'I rischi da mappare',
        cards: [
          { title: 'Danni a terzi', text: 'Un cliente che si fa male nei tuoi locali o per un tuo errore: la RC aziendale è spesso la prima copertura da attivare.' },
          { title: 'Errori professionali', text: 'Se offri consulenza o servizi, un errore può costare caro: valuta la RC professionale.' },
          { title: 'Danni ai beni', text: 'Incendio, furto, acqua su locali, attrezzature e merce.' },
          { title: 'Malattia del titolare', text: 'Se l\u2019attività dipende da te, la perdita di reddito per malattia è un rischio di continuità.' },
          { title: 'Cyber e dati', text: 'Se tratti dati o dipendi dai sistemi, un attacco può fermarti.' },
          { title: 'Controversie', text: 'Clienti che non pagano, contestazioni contrattuali, problemi con il locatore.' },
        ],
      },
      {
        id: 'budget', type: 'text', title: 'Il budget assicurativo iniziale: come stabilire le priorità',
        paragraphs: [
          'In fase di avvio le risorse sono limitate: l\u2019ordine giusto è prima i rischi che possono compromettere la continuità (responsabilità, reddito del titolare), poi i rischi frequenti (danni ai beni), infine le estensioni accessorie.',
          'Un programma assicurativo ben costruito può crescere con l\u2019azienda: meglio poche coperture giuste che molte polizze accumulate senza una logica.',
        ],
      },
      {
        id: 'checklist-finale', type: 'checklist', title: 'La checklist finale',
        items: [
          'Forma giuridica scelta e registrata',
          'Collaboratori dichiarati e coperti (infortuni, LPP)',
          'Sede e beni assicurati',
          'Responsabilità civile analizzata e attivata',
          'Reddito del titolare protetto',
          'Previdenza valutata',
          'Piano di continuità predisposto',
        ],
      },
    ],
    faq: [
      { q: 'Quanto budget devo prevedere per le assicurazioni?', a: 'Dipende da attività, collaboratori e beni. In fase di business plan conviene stimare le coperture essenziali come costo fisso: ti aiutiamo a quantificarle prima di partire.' },
      { q: 'Serve tutto da subito?', a: 'No. Alcune coperture sono necessarie dal primo giorno (RC, infortuni se hai dipendenti), altre possono seguire la crescita. L\u2019importante è decidere consapevolmente, non per caso.' },
      { q: 'Cosa rischio se non assicuro niente?', a: 'I rischi restano tuoi: un danno a terzi o una malattia lunga possono superare di gran lunga il costo di anni di premi, proprio nella fase più fragile dell\u2019attività.' },
    ],
    cta: { title: 'Prepara il piano assicurativo della tua nuova attività', text: 'Prima dell\u2019apertura, definiamo insieme priorità, budget e coperture essenziali.', label: 'Prepara il piano' },
    related: ['/guide/aziende/forme-giuridiche-svizzera', '/soluzioni/aziende/tipologie-assicurazioni'],
  },
  {
    path: '/guide/aziende/forme-giuridiche-svizzera',
    kind: 'guide',
    area: 'Aziende',
    areaPath: '/soluzioni/aziende',
    eyebrow: 'Guida pratica · Aziende',
    title: 'Forme giuridiche in Svizzera: ditta individuale, Sagl, SA.',
    summary: 'Il confronto operativo, previdenziale e assicurativo tra le principali forme giuridiche.',
    updated: 'Giugno 2026',
    readingTime: '7 min',
    intro: [
      'La forma giuridica che scegli influenza responsabilità personale, previdenza e assicurazioni necessarie. Questa guida ne illustra le conseguenze pratiche, senza sostituire la consulenza legale e fiscale.',
    ],
    sections: [
      {
        id: 'confronto', type: 'cards', title: 'Le forme principali a confronto',
        cards: [
          { title: 'Ditta individuale', text: 'Semplice ed economica da avviare. Il titolare risponde con il patrimonio personale e organizza da sé infortuni, indennità e previdenza.' },
          { title: 'Sagl', text: 'Responsabilità limitata al capitale sociale. Il titolare occupato è assimilato a un dipendente per infortuni e previdenza.' },
          { title: 'SA', text: 'Struttura per progetti più grandi o con investitori. Capitale minimo più alto, governance più formale.' },
          { title: 'Società in nome collettivo', text: 'Per due o più persone fisiche: i soci rispondono solidalmente. Simile alla ditta individuale sul piano assicurativo.' },
        ],
      },
      {
        id: 'impatto', type: 'text', title: 'L\u2019impatto sul titolare',
        paragraphs: [
          'Con la ditta individuale, il titolare è un indipendente a tutti gli effetti: infortuni, indennità giornaliera e previdenza professionale sono da organizzare personalmente, e la responsabilità verso terzi ricade sul patrimonio privato.',
          'Con Sagl e SA, il titolare occupato nell\u2019azienda è di regola assimilato a un dipendente: infortuni e secondo pilastro passano dalla società. Resta da proteggere la responsabilità degli organi societari e il reddito personale.',
        ],
      },
      {
        id: 'domande', type: 'checklist', title: 'Le domande da porsi',
        items: [
          'Lavorerò da solo o con soci?',
          'Ho intenzione di assumere personale?',
          'Quale capitale posso investire all\u2019inizio?',
          'Quanto rischio personale voglio assumermi?',
          'Ho bisogno di separare patrimonio privato e aziendale?',
          'Prevedo investitori o una futura vendita?',
        ],
      },
      {
        id: 'limite', type: 'warning', title: 'Il limite di questa guida',
        text: 'La scelta della forma giuridica ha conseguenze legali, fiscali e contabili che richiedono il coinvolgimento di fiduciario, notaio o consulente legale. Il nostro ruolo è chiarirne le conseguenze assicurative e previdenziali.',
      },
    ],
    faq: [
      { q: 'Conviene partire con la ditta individuale?', a: 'Per molte attività in avvio sì: è semplice e poco costosa. La trasformazione in Sagl o SA è possibile in seguito, quando fatturato e rischi crescono.' },
      { q: 'Con la Sagl sono protetto personalmente?', a: 'La responsabilità è di regola limitata al capitale sociale, ma restano eccezioni e la responsabilità degli organi. Anche il tuo reddito e la tua previdenza vanno organizzati tramite la società.' },
      { q: 'Cosa cambia per le assicurazioni tra ditta individuale e Sagl?', a: 'Cambia il tuo status: da indipendente (coperture personali da attivare) ad assimilato dipendente (infortuni e LPP tramite la società). Anche le polizze vanno intestate correttamente.' },
    ],
    cta: { title: 'Valuta le conseguenze assicurative della forma scelta', text: 'Prima di registrare l\u2019attività, chiariamo cosa cambia per te sul piano assicurativo e previdenziale.', label: 'Valuta le conseguenze' },
    related: ['/guide/aziende/aprire-un-azienda', '/guide/privati/mettersi-in-proprio'],
  },
];

export const PREVIDENZA_PAGE = {
  path: '/soluzioni/previdenza',
  kind: 'area',
  area: 'Previdenza',
  areaPath: '/soluzioni/previdenza',
  eyebrow: 'Area Previdenza',
  title: 'Previdenza in Svizzera: AVS, LPP, pilastro 3a e protezione del reddito.',
  summary: 'Analizza AVS, cassa pensione, pilastro 3a, invalidità e protezione della famiglia per costruire un piano previdenziale sostenibile nel tempo.',
  seoTitle: 'Previdenza in Svizzera: LPP e pilastro 3a',
  seoDescription: 'Analisi previdenziale in Svizzera: AVS, LPP, pilastro 3a e 3b, lacune, invalidità e pensionamento. Leggi il certificato e pianifica le priorità.',
  keywords: ['previdenza svizzera', 'LPP', 'pilastro 3a', 'analisi previdenziale', 'cassa pensione svizzera'],
  image: '/assets/soluzione-previdenza.png',
  updated: 'Giugno 2026',
  readingTime: '6 min',
  intro: [
    'In Svizzera la previdenza si costruisce su tre pilastri: statale, professionale e privata. Capire come si combinano — e dove si aprono le lacune — è il primo passo per proteggere il tuo reddito futuro.',
    'Una lettura coordinata di AVS, certificato LPP, pilastro 3a, patrimonio e coperture di rischio aiuta a stimare scenari di pensione, invalidità e decesso prima di scegliere nuovi strumenti.',
  ],
  sections: [
    {
      id: 'tre-livelli', type: 'cards', title: 'I tre livelli della previdenza',
      cards: [
        { title: 'Primo pilastro: AVS/AI', text: 'La previdenza statale di base. Copre le esigenze vitali, ma raramente basta a mantenere il tenore di vita.' },
        { title: 'Secondo pilastro: LPP', text: 'La previdenza professionale tramite il datore di lavoro. Per gli indipendenti va organizzata volontariamente.' },
        { title: 'Terzo pilastro', text: 'La previdenza privata: 3a vincolata con vantaggi fiscali, 3b libera. Qui si chiudono le lacune.' },
      ],
    },
    {
      id: 'domande', type: 'checklist', title: 'Le domande principali',
      items: [
        'Quale reddito avrò in pensione?',
        'Cosa succede se non posso più lavorare?',
        'La mia famiglia è protetta in caso di decesso?',
        'Quanto posso risparmiare ogni mese in modo sostenibile?',
        'Ho cambiato lavoro o iniziato un\u2019attività indipendente?',
        'Sto acquistando un\u2019abitazione?',
      ],
    },
    {
      id: 'protezione-prima', type: 'text', title: 'Protezione prima del risparmio',
      paragraphs: [
        'Molti iniziano a risparmiare senza aver protetto il reddito presente. Se domani non puoi più lavorare, il piano di risparmio si ferma: per questo la pianificazione seria parte da incapacità di guadagno, rischio decesso e liquidità di emergenza, e solo dopo guarda al rendimento.',
      ],
    },
    {
      id: 'situazioni', type: 'cards', title: 'Percorsi per situazione',
      cards: [
        { title: 'Dipendente', text: 'Coordinare LPP e terzo pilastro, verificare le lacune e i vantaggi fiscali del 3a.' },
        { title: 'Indipendente', text: 'Senza datore di lavoro tutto va ricostruito: infortuni, indennità, secondo e terzo pilastro.' },
        { title: 'Coppia e famiglia', text: 'Protezione del reddito di entrambi, coordinamento con mutuo e figli.' },
        { title: 'Vicino alla pensione', text: 'Riscatti, anticipazioni, rendita o capitale: le decisioni degli ultimi anni contano molto.' },
      ],
    },
  ],
  faq: [
    { q: 'Qual è la differenza tra pilastro 3a e 3b?', a: 'Il 3a è vincolato, con vantaggi fiscali e limiti di versamento; il 3b è libero, senza limiti ma senza gli stessi benefici fiscali. Spesso si combinano.' },
    { q: 'Quanto posso versare nel terzo pilastro?', a: 'I limiti del 3a sono fissati ogni anno dalle autorità e variano tra dipendenti e indipendenti. Verifichiamo l\u2019importo aggiornato in consulenza.' },
    { q: 'Posso usare la previdenza per comprare casa?', a: 'In determinate condizioni è possibile prelevare o mettere in pegno secondo e terzo pilastro per l\u2019abitazione di proprietà, con effetti da valutare su previdenza e fiscalità.' },
    { q: 'Cosa succede se divento indipendente?', a: 'Il secondo pilastro non è più automatico: senza intervento si apre una lacuna che cresce ogni anno. Esistono soluzioni LPP per indipendenti e il 3a con limiti maggiorati.' },
  ],
  cta: { title: 'Richiedi un\u2019analisi previdenziale', text: 'Partiamo dalla tua situazione attuale: lacune, priorità e un piano realistico per il futuro.', label: 'Richiedi l\u2019analisi' },
  related: ['/guide/privati/comprare-casa-a-30-anni', '/guide/privati/mettersi-in-proprio'],
};
